import axios from 'axios'
import env from '@/env'

// The supervisor has no account, so this deliberately does not use the shared
// `$api` instance: that one attaches the signed-in user's Bearer and
// Verification-Token to every request, and a public link must never carry them.
// The only credential here is the emailed link token plus, after the code is
// verified, the approval session header.
const http = axios.create({
  baseURL: env.apiUrl,
  headers: { Accept: 'application/json' },
})

const SESSION_HEADER = 'X-Approval-Session'
const path = (token, rest = '') =>
  `/external-approval/${encodeURIComponent(token)}${rest}`
const withSession = (session) => ({ headers: { [SESSION_HEADER]: session } })

export default {
  show: (token) => http.get(path(token)).then((r) => r.data),
  sendCode: (token) => http.post(path(token, '/code')).then((r) => r.data),
  verify: (token, code) =>
    http.post(path(token, '/verify'), { code }).then((r) => r.data),
  // Opens this link with a dashboard session the supervisor already holds (the
  // same email), so a second link needs no second code.
  exchangeSession: (token, dashboardSession) =>
    http
      .post(path(token, '/session'), null, withSession(dashboardSession))
      .then((r) => r.data),
  sheet: (token, session) =>
    http.get(path(token, '/sheet'), withSession(session)).then((r) => r.data),
  decide: (token, session, body) =>
    http
      .post(path(token, '/decision'), body, withSession(session))
      .then((r) => r.data),
}

// ── The supervisor dashboard (every sheet addressed to one email) ────────────
// Same rules as above: its own client, no account credentials, one session header.
const base = '/external-supervisor'

export const supervisorDashboardApi = {
  sendCode: (email) => http.post(`${base}/code`, { email }).then((r) => r.data),
  verify: (email, code) =>
    http.post(`${base}/verify`, { email, code }).then((r) => r.data),
  list: (session, params = {}) =>
    http
      .get(`${base}/timesheets`, { params, ...withSession(session) })
      .then((r) => r.data),
  // The listed sheets (same filters as list) with their days, for export.
  exportSheets: (session, params = {}) =>
    http
      .get(`${base}/export`, { params, ...withSession(session) })
      .then((r) => r.data),
  bulkApprove: (session, body) =>
    http
      .post(`${base}/bulk-approve`, body, withSession(session))
      .then((r) => r.data),
}

// One sheet opened from the dashboard, shaped like the emailed-link API so the
// approval page can show and decide it unchanged. `id` stands where the link
// token does; the dashboard session is read from the tab by the caller.
export const dashboardSheetApi = {
  sheet: (id, session) =>
    http
      .get(`${base}/timesheets/${encodeURIComponent(id)}`, withSession(session))
      .then((r) => r.data),
  decide: (id, session, body) =>
    http
      .post(
        `${base}/timesheets/${encodeURIComponent(id)}/decision`,
        body,
        withSession(session),
      )
      .then((r) => r.data),
}
